/**
 * ASM-985: hooks the test editor's lock session plugs into, so the shared API client stays
 * free of editor logic.
 *
 * - `X-Edit-Session` is added to every request while a session is active (the provider
 *   returns null otherwise).
 * - While a session is active, the writes the server guards (GUARDED_WRITES: test, question,
 *   passage, clarifier and share saves) go out one at a time: the server claims each at the
 *   version in the header and bumps it, so a second one in flight would be refused. The
 *   header is computed when a write leaves the queue, after the previous response has
 *   updated the version. Every other request (searches, evaluation, renewals, releases)
 *   goes straight out; it still carries the header.
 * - Every response's `x-edit-lock` / `x-test-version`, every 409 carrying a lock code, and
 *   every 403 on a guarded write, is passed to the listener.
 */

export const EDIT_SESSION_HEADER = 'X-Edit-Session'
export const EDIT_LOCK_CODES = [
  'EDIT_LOCK_HELD',
  'EDIT_LOCK_STALE',
  'TEST_STALE',
  'EDIT_SESSION_ENDED',
]

// a write that never answers must not hold the queue for ever
const QUEUE_SLOT_TIMEOUT_MS = 120 * 1000

let provider = null
let listener = null
let queueTail = Promise.resolve()

export const setEditSessionProvider = (fn) => {
  provider = fn
}

export const setEditOutcomeListener = (fn) => {
  listener = fn
}

// the edu-api routes that read the edit session (routes/test.ts, testItem.js, passages.ts,
// contentSharing.ts), lock routes aside; keep in step with them
const ID = '[^/]+'
const GUARDED_WRITES = [
  ['post', '/test'],
  ['put', `/test/${ID}`],
  ['put', `/test/${ID}/publish`],
  ['delete', `/test/${ID}`],
  ['post', '/testitem'],
  ['put', `/testitem/${ID}`],
  ['delete', `/testitem/${ID}`],
  ['post', `/testitem/${ID}/duplicate`],
  ['post', '/testitem/bulk/update'],
  ['put', `/testitem/${ID}/correct-item-and-publish`],
  ['post', `/testitem/${ID}/question/${ID}/clarifier`],
  ['put', `/testitem/${ID}/question/${ID}/clarifier`],
  ['delete', `/testitem/${ID}/question/${ID}/clarifier/${ID}`],
  ['put', `/passage/${ID}`],
  ['post', `/passage/${ID}/duplicate`],
  ['post', `/content-sharing/${ID}`],
].map(([method, path]) => [method, new RegExp(`(?:^|/)${path.slice(1)}/?$`)])

const pathOf = (url = '') =>
  `${url}`.replace(/^[a-z]+:\/\/[^/]+/i, '').replace(/[?#].*$/, '')

export const isGuardedWrite = (config = {}) => {
  if (config.editLockBypass) return false
  const method = (config.method || 'get').toLowerCase()
  const path = pathOf(config.url)
  return GUARDED_WRITES.some(([m, re]) => m === method && re.test(path))
}

const takeQueueSlot = () => {
  let release
  const slot = new Promise((resolve) => {
    release = resolve
  })
  const ready = queueTail
  queueTail = queueTail.then(() =>
    Promise.race([
      slot,
      new Promise((resolve) => setTimeout(resolve, QUEUE_SLOT_TIMEOUT_MS)),
    ])
  )
  return ready.then(() => release)
}

const releaseSlot = (config) => {
  const release = config?.editSessionRelease
  if (release) {
    // eslint-disable-next-line no-param-reassign
    config.editSessionRelease = null
    release()
  }
}

/** axios request interceptor */
export const editSessionRequest = async (config) => {
  if (!provider || !provider()) return config
  if (isGuardedWrite(config)) {
    // eslint-disable-next-line no-param-reassign
    config.editSessionRelease = await takeQueueSlot()
  }
  const header = provider()
  // a release names the session it ends, which may not be the active one
  if (header && !config.headers?.[EDIT_SESSION_HEADER]) {
    // eslint-disable-next-line no-param-reassign
    config.headers[EDIT_SESSION_HEADER] = header
  }
  return config
}

// the session a request was sent for: the first part of its header
const sessionOf = (config) =>
  `${config.headers?.[EDIT_SESSION_HEADER] || ''}`.split(';')[0] || undefined

const report = (info) => {
  try {
    if (listener) listener(info)
  } catch (e) {
    console.warn('edit session listener failed', e)
  }
}

/** axios response interceptor (success) — registered before the app's own */
export const editSessionResponse = (response) => {
  const { config = {}, headers = {}, status } = response || {}
  if (config.headers?.[EDIT_SESSION_HEADER]) {
    report({
      session: sessionOf(config),
      status,
      lock: headers['x-edit-lock'],
      testVersion: headers['x-test-version'],
      url: config.url,
    })
  }
  releaseSlot(config)
  return response
}

/** axios response interceptor (error) — sees the raw axios error, rethrows it unchanged */
export const editSessionError = (error) => {
  const { config = {}, response } = error || {}
  const code = response?.data?.code
  if (
    config.headers?.[EDIT_SESSION_HEADER] &&
    response?.status === 409 &&
    EDIT_LOCK_CODES.includes(code)
  ) {
    report({
      session: sessionOf(config),
      status: 409,
      code,
      holder: response.data.holder,
      activeTestId: response.data.activeTestId,
      url: config.url,
    })
  } else if (
    config.headers?.[EDIT_SESSION_HEADER] &&
    response?.status === 403 &&
    isGuardedWrite(config)
  ) {
    report({ session: sessionOf(config), status: 403, url: config.url })
  } else if (
    config.headers?.[EDIT_SESSION_HEADER] &&
    response?.headers?.['x-test-version'] !== undefined
  ) {
    // a save that failed after its claim landed: the test moved, and the next save must
    // carry the new number or it is refused as stale
    report({
      session: sessionOf(config),
      status: response.status,
      testVersion: response.headers['x-test-version'],
      url: config.url,
    })
  }
  releaseSlot(config)
  return Promise.reject(error)
}

/** For a saga's catch: was this failure a lock refusal the session already handled? */
export const isEditLockRefusal = (err) =>
  err?.status === 409 && EDIT_LOCK_CODES.includes(err?.response?.data?.code)
